// src/components/sections/RelatedItem.jsx

import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";

import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";
import "swiper/css/pagination";

import { Autoplay } from "swiper/modules";

import { CartProduct } from "../../assets/icons/icons";
import { useTranslation } from "react-i18next";

const API_BASE = "https://laravel-api.aj-mart.com";

const buildStorageUrl = (p = "") =>
  `${API_BASE}/storage/app/public/${String(p || "").replace(/^\/+/, "")}`;

export default function RelatedItem({ currentId, category }) {
  const { t, i18n } = useTranslation();

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      setLoading(true);

      try {
        const res = await axios.get(`${API_BASE}/api/products`);

        let list =
          res?.data?.products ||
          res?.data?.data ||
          [];

        // تأكد أن البيانات Array
        if (!Array.isArray(list)) {
          list = [];
        }

        // استبعاد المنتج الحالي
        if (currentId !== undefined && currentId !== null) {
          list = list.filter(
            (p) => String(p?.product_id) !== String(currentId)
          );
        }

        // فلترة حسب التصنيف إذا موجود
        if (category) {
          const normalizedCategory = String(category)
            .trim()
            .toLowerCase();

          const sameCategoryProducts = list.filter((p) => {
            const productCategory = String(
              p?.product_category || ""
            )
              .trim()
              .toLowerCase();

            return productCategory === normalizedCategory;
          });

          // إذا في منتجات بنفس التصنيف اعرضها
          if (sameCategoryProducts.length > 0) {
            list = sameCategoryProducts;
          }
        }

        if (!cancelled) {
          setProducts(list.slice(0, 12));
        }
      } catch (error) {
        console.error("RelatedItem fetch error:", error);

        if (!cancelled) {
          setProducts([]);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    run();

    return () => {
      cancelled = true;
    };
  }, [currentId, category]);

  const getText = (ar, en) => {
    if (i18n.language === "ar") {
      return ar || en || "";
    }

    return en || ar || "";
  };

  if (loading) {
    return null;
  }

  if (!products.length) {
    return null;
  }

  return (
    <section className="pb-[40px]">
      <div className="container">

        {/* Title */}
        <div className="direction-text-ar page-title mb-[20px]">
          {t("Related")} <span>{t("Products")}</span>
        </div>

        <Swiper
          modules={[Autoplay]}
          spaceBetween={20}
          slidesPerView={1}
          style={{
            padding: "0 18px",
            direction: "ltr",
            height: "500px",
          }}
          loop={products.length > 4}
          autoplay={{
            delay: 2000,
            disableOnInteraction: false,
            pauseOnMouseEnter: true,
          }}
          breakpoints={{
            640: {
              slidesPerView: 1.5,
            },
            768: {
              slidesPerView: 2,
            },
            1024: {
              slidesPerView: 4,
            },
          }}
          className="h-[500px] px-[20px]"
        >
          {products.map((el) => {
            const productId = el?.product_id;

            const name =
              getText(
                el?.product_name_A,
                el?.product_name_E
              ) || "Product";

            const desc =
              getText(
                el?.product_desc_A,
                el?.product_desc_E
              ) || "";

            const price = Number(
              el?.product_price || 0
            );

            const discount = Math.max(
              0,
              Math.min(
                100,
                Number(
                  el?.product_price_discount || 0
                )
              )
            );

            const afterDiscount = Number(
              price * (1 - discount / 100)
            ).toFixed(2);

            const inStock =
              Number(
                el?.product_stock_available || 0
              ) > 0;

            const inventoryClass = inStock
              ? "text-[#18AB00]"
              : "text-red-500";

            const img = el?.product_main_img
              ? buildStorageUrl(
                  el.product_main_img
                )
              : "https://placehold.co/300x300?text=No+Image";

            if (!productId) {
              return null;
            }

            return (
              <SwiperSlide
                key={productId}
                className="flex justify-center"
              >
                <Link
                  to={`/productdetails/${productId}`}
                  className="
                    flex
                    flex-col
                    h-auto
                    bg-[#f6f6f6]
                    w-[250px]
                    overflow-hidden
                    border
                    justify-between
                    border-[#d1d1d1]
                    rounded-[4px]
                    transition-all
                    duration-300
                    hover:shadow-lg
                    hover:-translate-y-1
                  "
                >
                  {/* Image */}
                  <div className="w-[250px] h-[250px] relative">
                    <div className="w-[250px] h-[250px] border-b border-b-[#1485a6] bg-white">
                      <img
                        className="
                          w-full
                          h-full
                          object-contain
                          transition-transform
                          duration-300
                          hover:scale-[1.03]
                        "
                        src={img}
                        alt={name}
                        loading="lazy"
                      />
                    </div>
                  </div>

                  {/* Product Info */}
                  <div className="p-[10px] flex-1">
                    <div
                      className="
                        text-[18px]
                        font-bold
                        text-center
                        capitalize
                        line-clamp-2
                        min-h-[54px]
                      "
                    >
                      {name}
                    </div>

                    {desc && (
                      <p
                        className="
                          direction-ar-rt
                          text-[13px]
                          text-[#666666]
                          text-center
                          min-h-[40px]
                          mt-1
                        "
                      >
                        {desc.length > 75
                          ? `${desc.substring(0, 75)}...`
                          : desc}
                      </p>
                    )}

                    {/* Price */}
                    <div
                      className="
                        flex
                        items-center
                        justify-center
                        gap-[5px]
                        text-center
                        mt-2
                      "
                    >
                      {discount > 0 ? (
                        <>
                          <span className="font-bold text-[#1485a6]">
                            {afterDiscount} {t("JOD")}
                          </span>

                          <span className="line-through text-gray-400 text-sm">
                            {price.toFixed(2)} {t("JOD")}
                          </span>
                        </>
                      ) : (
                        <span className="font-bold text-[#1485a6]">
                          {price.toFixed(2)} {t("JOD")}
                        </span>
                      )}
                    </div>

                    {/* Stock */}
                    <div
                      className={`
                        text-center
                        font-bold
                        mt-1
                        ${inventoryClass}
                      `}
                    >
                      {t(
                        inStock
                          ? "In Stock"
                          : "Out of Stock"
                      )}
                    </div>
                  </div>

                  {/* Button */}
                  <div
                    className="
                      bg-[#1485a6]
                      flex
                      p-2
                      items-center
                      justify-center
                      gap-1.5
                      text-white
                      font-bold
                      rounded-tl-[20px]
                      rounded-tr-[20px]
                      transition-opacity
                      duration-300
                      hover:opacity-90
                    "
                  >
                    <CartProduct />

                    <span>
                      {t("View Product")}
                    </span>
                  </div>
                </Link>
              </SwiperSlide>
            );
          })}
        </Swiper>
      </div>
    </section>
  );
}